import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, setTabToken } from "@/lib/api";

const AuthContext = createContext(null);

/* v3.2.2 · Per-tab session mode.
   When a URL query flag `?tab_session=1` is present, we opt-in to
   sessionStorage-based auth: login stores the access_token in
   sessionStorage (scoped per-tab), and axios sends it as a Bearer
   header on every request (bypassing the shared cookie). This lets
   two tabs on the same browser be signed in as different users. */

function isTabSessionMode() {
  try {
    return new URLSearchParams(window.location.search).has("tab_session");
  } catch { return false; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous, object = logged in
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Persist the token per-tab when the caller opted-in via URL flag.
      // We also store it whenever tab-session mode is set so subsequent
      // requests in this tab keep using the Bearer header.
      if (isTabSessionMode() && data.access_token) {
        setTabToken(data.access_token);
      }
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const register = async (email, password, name, role) => {
    setError("");
    try {
      // v3.2.47 · Pull UTM attribution captured earlier by the visit
      // tracker so /admin/visitors/campaign/{name} can compute a real
      // conversion rate. Silent no-op when the storage isn't available.
      let utms = {};
      try {
        const s = sessionStorage.getItem("eengdes_visit_utms");
        if (s) utms = JSON.parse(s) || {};
      } catch { /* ignore */ }
      const { data } = await api.post("/auth/register", {
        email, password, name, role,
        utm_source:   utms.utm_source   || null,
        utm_medium:   utms.utm_medium   || null,
        utm_campaign: utms.utm_campaign || null,
      });
      if (isTabSessionMode() && data.access_token) {
        setTabToken(data.access_token);
      }
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.warn("Logout failed:", err); }
    setTabToken(null);           // always clear per-tab token
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
